import { Button, makeStyles, Typography } from '@material-ui/core';
import red from '@material-ui/core/colors/red';
import React, { useEffect, useState } from 'react';

import { ReactComponent as CreatePostSvg } from '../../../../assets/create_post.svg';
import {
  ChannelInput,
  FullPlexFragment,
  PlexInput,
  PlexInputError,
  PlexMember,
  PlexMemberRole,
} from '../../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { checkRequiredFields } from '../../../../utils/form';
import { Loader } from '../../../shared/Loader';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { DialogActions } from '../DialogActions';
import { DialogTopBar } from '../DialogTopBar';
import { PlexDialogContent, PlexFormData } from './PlexDialogContent';
import { plexMemberInput } from './plexUserRoleTargetKey';

type Props = {
  mode: 'create' | 'update';
  plex:
    | Pick<
        FullPlexFragment,
        'name' | 'color' | 'description' | 'settings' | 'channels' | 'currentUserRelation' | 'googleCalendars'
      >
    | undefined;
  onSave: (plex: PlexInput) => void;
  saveLoading: boolean;
  saveError: PlexInputError | null;
  onDelete?: () => void;
};

export function PlexDialog(props: Props): React.ReactElement {
  const { mode, plex, saveLoading, saveError, onSave, onDelete } = props;
  const classes = useStyles();
  const [state, setState] = useState<PlexFormData | undefined>();

  useEffect(() => {
    if (state == null && plex != null) {
      setState({
        name: plex.name,
        color: plex.color,
        description: plex.description || '',
        googleGroups: null,
        userRoles: plex.settings?.members || [],
        channels: plex.channels || [],
        googleCalendars: plex.googleCalendars || [],
        isCurrentUserOwner: plex.currentUserRelation?.role === PlexMemberRole.Owner,
      });
    }
  }, [plex, state]);

  if (saveLoading) {
    // TODO: show loader
  }

  if (saveError) {
    // TODO: set error state
  }

  const handlePlexFormChange = React.useCallback(
    (changeCallback: (prevFormData: PlexFormData) => PlexFormData): void => {
      setState((prevState) => (prevState !== undefined ? changeCallback(prevState) : prevState));
    },
    [setState]
  );

  const handleSave = (): void => {
    if (state != null) {
      const userRoles = state.userRoles.map(plexMemberInput);
      const existingMembersList: { [key: string]: PlexMember } = {};
      plex?.settings.members.forEach((member) => {
        existingMembersList[plexMemberInput(member).id] = member;
      });

      const newMemberIds: Set<string> = new Set(userRoles.map((u) => u.id));

      // New members are either not in the existing list or their role was changed
      const addedMembers = userRoles.filter(
        (member) => !existingMembersList[member.id] || existingMembersList[member.id].role !== member.role
      );
      const removedMembers =
        plex?.settings.members.filter((m) => !newMemberIds.has(plexMemberInput(m).id)).map((m) => plexMemberInput(m)) ||
        [];

      const saveData: PlexInput = {
        name: state.name,
        color: state.color,
        description: state.description,
        addedMembers,
        removedMembers,
        channels: state.channels.map(({ type, name, id, externalId }: ChannelInput) => ({
          type,
          name,
          id,
          externalId,
        })),
        googleCalendars: state.googleCalendars.map(({ id, googleCalendarId }) => ({ id, googleCalendarId })),
      };

      checkRequiredFields(saveData, ['name']);
      onSave(saveData);
    }
  };

  const handlePlexDelete = (): void => {
    if (onDelete != null) {
      onDelete();
    }
  };

  const saveEnabled = plex != null && !saveLoading;

  // TODO: form input validation
  // FIXME: Dialog usage causes a console warning, more details in https://github.com/mui-org/material-ui/issues/13394
  return (
    <div className={classes.root}>
      <DialogTopBar>
        <PlexTypography className={classes.rootTitle} size={TextSizesEnum.XXLarge}>
          <CreatePostSvg className={classes.rootTitleIcon} />
          {mode === 'create' ? 'Create Plex' : 'Edit Plex'}
        </PlexTypography>
      </DialogTopBar>

      <div className={classes.content}>
        {state != null && (
          <PlexDialogContent
            plex={state}
            mode={mode}
            saveError={saveError}
            onPlexChange={handlePlexFormChange}
            onPlexDelete={handlePlexDelete}
          />
        )}
        {plex == null && <Loader />}
      </div>

      <DialogActions
        cta={
          <Button disabled={!saveEnabled} onClick={handleSave} color="primary" variant="contained">
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        }
      >
        {/* TODO: Make error state prettier */}
        {saveError != null && (
          <Typography className={classes.errorState} variant="body1">
            Could not save plex due to error
          </Typography>
        )}
      </DialogActions>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    overflow: 'hidden',
    flexDirection: 'column',
    height: '100%',
  },
  rootTitle: {
    fontWeight: 'bold',
    marginLeft: spacings.xxlarge,
    marginTop: spacings.xlarge,
    marginBottom: spacings.xlarge,
    color: colorPalette.dark._800,
  },
  rootTitleIcon: {
    width: 32,
    height: 32,
    marginRight: spacings.normal,
  },
  content: {
    flex: '1 1 auto',
    [`overflow-y`]: 'auto',
  },
  errorState: {
    color: red[600],
  },
}));
